import type { IUploadProgressEntry } from "@frontend-shared/types";
import { createContext } from "react";

import type { IChatContext } from "@/pages/chat/context/ChatContext";

// NOTE: NSAI-7067 — the second half of "a transfer outlives its page", and the
// web twin of mobile's TransferContext (NSAI-5241). `MeetingUploadProvider`
// lifted the UPLOAD store above the routes in NSAI-5141; this lifts the
// connector FETCH that precedes it.
//
// NOTE: Why the fetch had to move too. The file pulled out of Google Drive was
// written into a `ChatContextProvider` mounted INSIDE the Uploads page's
// picker, and the hand-off from "downloaded" to "uploading" was an effect in
// that same subtree. Leaving the page unmounted both: the download promise
// kept running with nowhere to report to, the upload never started, and the
// only defence was a "Fetching a file in progress — leaving will cancel it"
// dialog. Lifting the store alone cannot reach that; the hand-off itself has
// to live above the routes.
//
// NOTE: A separate context from `MeetingUploadContext` on purpose, as on
// mobile: that contract is mirrored one-for-one across the two platforms.
export interface ITransferContext {
  // NOTE: Uploads and connector fetches as one list, in the order the Uploads
  // page renders them — fetches last, so a file keeps its place as it moves
  // from one owner to the other.
  transfers: IUploadProgressEntry[];
  // NOTE: Aborts a transfer that is still running and dismisses one that has
  // settled. Held here because only this layer knows which owner — the
  // connector picker or the meeting uploader — holds the entry.
  cancelTransfer: (entry: IUploadProgressEntry) => void;
  // NOTE: The picker has no state of its own for a queued file (the attachment
  // it renders from is dropped the moment the uploader accepts it), so it
  // closes on this signal instead (NSAI-5134). A subscription rather than a
  // prop, because the hand-off now happens above the picker.
  registerUploadQueuedListener: (listener: () => void) => () => void;
  // NOTE: The attachment store the Uploads page's connector picker writes
  // into. It lives in this provider, not in a `ChatContextProvider` around the
  // picker, which is the whole fix: the picker re-provides THIS value, so a
  // file it starts fetching is owned by something that survives the page.
  // Kept off the app-wide tree on purpose — mounting it as a root
  // ChatContextProvider (mobile's shape) would let any component rendered
  // outside its chat page read upload-picker state instead of throwing.
  pickerChatContext: IChatContext;
}

export const TransferContext = createContext<ITransferContext | null>(null);
